import React, { useEffect, useMemo, useRef, useState } from 'react';
import pluralize from 'pluralize';
import { useMutation } from '@tanstack/react-query';

import { APIbulkUpdateSprkImages } from '../../../api/Assets';

import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type { Asset } from '../../../types/asset';
import type { AssetGroup } from '../../../types/asset-group';

import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';

type Membership = 'all' | 'some' | 'none';

interface AssetGroupsBulkModalProps {
  assets: Asset[];
  assetGroups: AssetGroup[];
  show: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
};

const AssetGroupsBulkModal: React.FC<AssetGroupsBulkModalProps> = ({ assets, assetGroups, show, onConfirm, onDismiss }) => {
  const dialogRef = useRef<HTMLElement>(null);

  const initialCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    assets.forEach(asset => asset.groups?.forEach(group => {
      counts[String(group.id)] = (counts[String(group.id)] ?? 0) + 1;
    }));
    return counts;
  }, [assets]);

  const initialMembership = useMemo(() => {
    const membership: Record<string, Membership> = {};
    assetGroups.forEach(group => {
      const count = initialCounts[String(group.id)] ?? 0;
      membership[String(group.id)] = count === 0 ? 'none' : count === assets.length ? 'all' : 'some';
    });
    return membership;
  }, [assetGroups, assets.length, initialCounts]);

  const [membership, setMembership] = useState(initialMembership);

  const addGroups = assetGroups.map(group => String(group.id))
    .filter(id => membership[id] === 'all' && initialMembership[id] !== 'all');
  const removeGroups = assetGroups.map(group => String(group.id))
    .filter(id => membership[id] === 'none' && initialMembership[id] !== 'none');
  const hasChanges = addGroups.length > 0 || removeGroups.length > 0;
  const hasMixedGroups = Object.values(initialMembership).includes('some');

  const { mutate: save, isPending: saving, error } = useMutation({
    mutationFn: () => APIbulkUpdateSprkImages({
      action: 'groups',
      ids: assets.map(asset => asset.id),
      add_groups: addGroups,
      remove_groups: removeGroups,
    }),
    onSuccess: onConfirm,
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    const handleHide = (event: Event) => {
      if (event.target !== dialog) {
        return;
      }
      if (saving) {
        event.preventDefault();
      } else {
        onDismiss();
      }
    };
    dialog?.addEventListener('wa-hide', handleHide);
    return () => dialog?.removeEventListener('wa-hide', handleHide);
  }, [saving, onDismiss]);

  /** Partly assigned groups cycle through all → none → back to unchanged; the others just toggle. */
  const toggleGroup = (groupId: string) => {
    setMembership(current => {
      const state = current[groupId];
      let next: Membership;
      if (state === 'some') {
        next = 'all';
      } else if (state === 'all') {
        next = 'none';
      } else {
        next = initialMembership[groupId] === 'some' ? 'some' : 'all';
      }
      return { ...current, [groupId]: next };
    });
  };

  return (
    <wa-dialog
      ref={(dialog) => { dialogRef.current = dialog; }}
      label={`Change groups of ${pluralize('image', assets.length, true)}`}
      id="dialog-bulk-asset-groups"
      open={show}
    >
      <div className="wa-stack">
        {hasMixedGroups && (
          <p className="bulk-groups-hint">
            Partly checked groups contain only some of the selected images. Leave them partly checked to keep them as they are,
            or click to add all selected images to the group or remove them from it.
          </p>
        )}
        {assetGroups.length === 0 && <p>There are no image groups yet.</p>}
        <div className="wa-stack wa-gap-xs">
          {assetGroups.map(group => {
            const groupId = String(group.id);
            const state = membership[groupId];
            const initialCount = initialCounts[groupId] ?? 0;
            return (
              <wa-checkbox
                key={groupId}
                ref={(checkbox: WaCheckbox | null) => {
                  // Keep the web component in sync, because it changes its own checked/indeterminate state on click.
                  if (checkbox) {
                    checkbox.checked = state === 'all';
                    checkbox.indeterminate = state === 'some';
                  }
                }}
                disabled={saving}
                onChange={() => toggleGroup(groupId)}
              >
                {group.name}
                {initialMembership[groupId] === 'some' && (
                  <span className="bulk-groups-count"> ({initialCount} of {assets.length})</span>
                )}
              </wa-checkbox>
            );
          })}
        </div>
        {error && <div className="error-message" role="alert">{error.message || 'Failed to update groups. Please try again.'}</div>}
      </div>
      <wa-button slot="footer" size="s" appearance="outlined" disabled={saving} onClick={onDismiss}>
        Cancel
      </wa-button>
      <wa-button slot="footer" size="s" variant="brand" disabled={saving || !hasChanges} onClick={() => save()}>
        {saving ? 'Saving...' : 'Save changes'}
      </wa-button>
    </wa-dialog>
  );
}

export default AssetGroupsBulkModal;
